import { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { MODEL_BADGE_COLORS, normalizeModelId, getModelName } from "../lib/aiModels";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, MessageSquare, Sparkles, Trash2, Edit, Share2, Send,
  Bot, Crown, Zap, BarChart3, Search, LayoutGrid, List,
  Copy, Check, Clock, Cpu, X, Pencil, ArrowLeft, Loader2
} from "lucide-react";

const TIER_COLORS = {
  basic: "bg-muted/70 text-muted-foreground border-border",
  standard: "bg-blue-500/10 text-blue-400 border-blue-500/30",
  premium: "bg-violet-500/10 text-violet-400 border-violet-500/30",
  enterprise: "bg-amber-500/10 text-amber-400 border-amber-500/30",
};

const MODEL_STRIPE = {
  llama: "border-l-blue-400", deepseek: "border-l-cyan-400", qwen: "border-l-violet-400",
  mistral: "border-l-orange-400", gemini: "border-l-teal-400", kimi: "border-l-rose-400",
  glm: "border-l-slate-400", gptoss: "border-l-emerald-400",
};

const PURPOSE_LABELS = {
  best_friend: "Your personal companion for daily chats",
  accountant: "Financial guidance and tax help",
  personal_shopper: "Shopping recommendations and deals",
  therapist: "Supportive mental wellness conversations",
  tutor: "Learning support across all subjects",
  fitness_coach: "Workout plans and fitness guidance",
  legal_advisor: "Legal information and guidance",
  travel_planner: "Trip planning and travel tips",
  writing_partner: "Creative writing collaboration",
  tech_support: "Technical troubleshooting help",
  custom: "Custom-built for your specific needs",
};

const PURPOSE_GRADIENTS = {
  best_friend: "from-pink-500 to-rose-600",
  accountant: "from-blue-500 to-sky-600",
  personal_shopper: "from-rose-500 to-pink-600",
  therapist: "from-violet-500 to-violet-500",
  tutor: "from-emerald-500 to-teal-600",
  fitness_coach: "from-orange-500 to-amber-600",
  legal_advisor: "from-slate-500 to-gray-600",
  travel_planner: "from-teal-500 to-cyan-600",
  writing_partner: "from-purple-500 to-violet-600",
  tech_support: "from-gray-500 to-slate-600",
  custom: "from-cyan-400 to-violet-500",
};

function formatRelativeTime(iso) {
  if (!iso) return "Never";
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "Just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString([], { month: "short", day: "numeric" });
}

function AnimatedNumber({ value, duration = 800 }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (typeof value !== "number" || isNaN(value)) { setDisplay(value); return; }
    if (value === 0) { setDisplay(0); return; }
    let start = 0;
    const step = Math.max(1, Math.ceil(value / (duration / 16)));
    const timer = setInterval(() => {
      start = Math.min(start + step, value);
      setDisplay(start);
      if (start >= value) clearInterval(timer);
    }, 16);
    return () => clearInterval(timer);
  }, [value, duration]);
  return <>{display}</>;
}

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [assistants, setAssistants] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Search & Filter
  const [searchQuery, setSearchQuery] = useState("");
  const [filterModel, setFilterModel] = useState("");
  const [filterPurpose, setFilterPurpose] = useState("");
  const [viewMode, setViewMode] = useState("grid"); // grid | list

  // Inline rename
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const renameRef = useRef(null);

  // Copy URL
  const [copiedSlug, setCopiedSlug] = useState(null);

  // AI auto-post
  const [aiPostModal, setAiPostModal] = useState(null);
  const [aiPostText, setAiPostText] = useState("");

  const loadData = () => {
    setLoading(true);
    setLoadError(null);
    Promise.all([api.get("/assistants"), api.get("/user/stats")])
      .then(([a, s]) => { setAssistants(a.data); setStats(s.data); })
      .catch(() => setLoadError("Failed to load dashboard data."))
      .finally(() => setLoading(false));
  };
  useEffect(loadData, []);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/assistants/${deleteTarget.id}`);
      toast.success(`${deleteTarget.name} has been deleted`);
      setDeleteTarget(null);
      loadData();
    } catch { toast.error("Failed to delete assistant"); }
    finally { setDeleting(false); }
  };

  const startRename = (a) => {
    setRenamingId(a.id);
    setRenameValue(a.name);
    setTimeout(() => renameRef.current?.focus(), 50);
  };

  const saveRename = async (a) => {
    if (!renameValue.trim() || renameValue.trim() === a.name) { setRenamingId(null); return; }
    try {
      await api.put(`/assistants/${a.id}`, { name: renameValue.trim() });
      toast.success("Renamed successfully");
      loadData();
    } catch { toast.error("Failed to rename"); }
    setRenamingId(null);
  };

  const copyUrl = (slug) => {
    navigator.clipboard.writeText(`${window.location.origin}/${slug}`);
    setCopiedSlug(slug);
    toast.success("URL copied to clipboard");
    setTimeout(() => setCopiedSlug(null), 2000);
  };

  const handleGenerateAIPost = async (assistant) => {
    setAiPostModal({ assistant, loading: true });
    setAiPostText("");
    try {
      const r = await api.post(`/ai-posts/generate/${assistant.id}`);
      setAiPostText(r.data.preview_content);
      setAiPostModal({ assistant, loading: false });
    } catch { toast.error("Failed to generate post"); setAiPostModal(null); }
  };

  const handlePublishAIPost = async () => {
    if (!aiPostModal || !aiPostText.trim()) return;
    try {
      await api.post(`/ai-posts/publish/${aiPostModal.assistant.id}`, { content_text: aiPostText.trim() });
      toast.success(`Posted by ${aiPostModal.assistant.name}!`);
      setAiPostModal(null);
    } catch { toast.error("Failed to publish"); }
  };

  // Filter assistants
  const filtered = assistants.filter((a) => {
    if (searchQuery && !a.name.toLowerCase().includes(searchQuery.toLowerCase()) && !a.slug.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (filterModel && a.ai_model !== filterModel) return false;
    if (filterPurpose && a.purpose !== filterPurpose) return false;
    return true;
  });

  const uniqueModels = [...new Set(assistants.map((a) => a.ai_model))];
  const uniquePurposes = [...new Set(assistants.map((a) => a.purpose))];

  return (
    <div className="min-h-screen" data-testid="dashboard-page">
      <div className="max-w-6xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 animate-fade-in-up">
          <div>
            <Link to="/feed" className="flex items-center gap-1 text-xs text-muted-foreground hover:text-cyan-400 no-underline mb-2 transition-colors" data-testid="back-to-feed">
              <ArrowLeft className="w-3 h-3" /> Back to Feed
            </Link>
            <p className="text-xs uppercase tracking-[0.2em] text-cyan-400 font-semibold mb-1" data-testid="dashboard-label">Dashboard</p>
            <h1 className="font-outfit text-2xl sm:text-3xl font-medium text-foreground" data-testid="dashboard-heading">My AI Companions</h1>
          </div>
          <Button data-testid="create-assistant-btn" onClick={() => navigate("/onboarding")} className="bg-gradient-to-r from-cyan-400 to-violet-500 text-white rounded-lg px-5 h-10 text-sm font-medium shadow-glow-sm hover:-translate-y-0.5 transition-all press-down">
            <Plus className="w-4 h-4 mr-2" /> Create New Companion
          </Button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="stats-bar">
            {[
              { label: "Companions", value: stats.assistant_count, icon: Bot, color: "text-white bg-gradient-to-br from-cyan-400 to-violet-500", iconColor: "text-white bg-white/20" },
              { label: "Messages", value: stats.total_messages, icon: MessageSquare, color: "text-white bg-gradient-to-br from-emerald-500 to-teal-600", iconColor: "text-white bg-white/20" },
              { label: "Conversations", value: stats.total_conversations, icon: BarChart3, color: "text-white bg-gradient-to-br from-violet-500 to-violet-500", iconColor: "text-white bg-white/20" },
              { label: "Plan", value: stats.current_tier ? stats.current_tier.charAt(0).toUpperCase() + stats.current_tier.slice(1) : "None", icon: Crown, color: "text-white bg-gradient-to-br from-amber-500 to-orange-600", iconColor: "text-white bg-white/20" },
            ].map((s, i) => (
              <div key={i} className={`${s.color} border-0 rounded-xl p-4 flex items-center gap-3 card-hover animate-fade-in-up stagger-${i + 1}`} data-testid={`stat-card-${s.label.toLowerCase()}`}>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${s.iconColor}`}><s.icon className="w-5 h-5" /></div>
                <div>
                  <div className="text-lg font-semibold font-outfit text-white">{typeof s.value === "number" ? <AnimatedNumber value={s.value} /> : s.value}</div>
                  <div className="text-xs text-white/70">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Error */}
        {loadError && (
          <div className="mb-6 bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3 flex items-center justify-between animate-fade-in" data-testid="dashboard-load-error">
            <span className="text-sm text-red-400">{loadError}</span>
            <button onClick={loadData} className="text-xs text-cyan-400 hover:text-cyan-300 bg-transparent border-0 cursor-pointer ml-4 font-medium press-down">Retry</button>
          </div>
        )}

        {/* Search / Filter / View Toggle */}
        {!loading && assistants.length > 0 && (
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6 animate-fade-in-up" data-testid="filter-bar">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/70" />
              <input
                data-testid="search-input"
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search companions..."
                className="w-full pl-10 pr-4 py-2 bg-card border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-cyan-400/25 focus:border-cyan-400"
              />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <select data-testid="filter-model" value={filterModel} onChange={(e) => setFilterModel(e.target.value)} className="px-3 py-2 bg-card border border-border rounded-lg text-xs text-foreground/80 focus:outline-none focus:ring-2 focus:ring-cyan-400/25 cursor-pointer">
                <option value="">All Models</option>
                {uniqueModels.map((m) => <option key={m} value={m}>{getModelName(m)}</option>)}
              </select>
              <select data-testid="filter-purpose" value={filterPurpose} onChange={(e) => setFilterPurpose(e.target.value)} className="px-3 py-2 bg-card border border-border rounded-lg text-xs text-foreground/80 focus:outline-none focus:ring-2 focus:ring-cyan-400/25 cursor-pointer">
                <option value="">All Purposes</option>
                {uniquePurposes.map((p) => <option key={p} value={p}>{p.replace(/_/g, " ")}</option>)}
              </select>
              <div className="flex bg-card border border-border rounded-lg overflow-hidden">
                <button onClick={() => setViewMode("grid")} className={`p-2 border-0 cursor-pointer transition-colors ${viewMode === "grid" ? "bg-cyan-500/10 text-cyan-400" : "bg-transparent text-muted-foreground/70 hover:text-muted-foreground"}`} data-testid="view-grid-btn"><LayoutGrid className="w-4 h-4" /></button>
                <button onClick={() => setViewMode("list")} className={`p-2 border-0 cursor-pointer transition-colors ${viewMode === "list" ? "bg-cyan-500/10 text-cyan-400" : "bg-transparent text-muted-foreground/70 hover:text-muted-foreground"}`} data-testid="view-list-btn"><List className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="dashboard-skeleton">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-card border border-border/60 rounded-xl p-6">
                <div className="flex items-center gap-4 mb-4"><div className="w-12 h-12 rounded-xl skeleton" /><div className="flex-1"><div className="h-5 w-32 skeleton mb-2" /><div className="h-3 w-48 skeleton" /></div></div>
                <div className="h-3 w-full skeleton mb-2" /><div className="h-3 w-2/3 skeleton mb-4" />
                <div className="flex gap-2"><div className="h-9 flex-1 skeleton" /><div className="h-9 w-20 skeleton" /><div className="h-9 w-9 skeleton" /></div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 && assistants.length > 0 ? (
          <div className="bg-card border border-border/60 rounded-2xl p-12 text-center animate-fade-in" data-testid="no-results">
            <Search className="w-8 h-8 text-muted-foreground/60 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No companions match your filters</p>
            <button onClick={() => { setSearchQuery(""); setFilterModel(""); setFilterPurpose(""); }} className="text-xs text-cyan-400 hover:text-cyan-300 bg-transparent border-0 cursor-pointer mt-2 font-medium">Clear filters</button>
          </div>
        ) : assistants.length === 0 ? (
          <div className="bg-card border border-border/60 rounded-2xl p-16 text-center animate-fade-in-up" data-testid="empty-state">
            <div className="w-20 h-20 mx-auto mb-5 rounded-2xl bg-gradient-to-br from-cyan-500/10 to-violet-500/10 border border-cyan-500/20 flex items-center justify-center">
              <Sparkles className="w-10 h-10 text-cyan-400 animate-float" />
            </div>
            <h2 className="font-outfit text-xl font-medium text-foreground mb-2">No companions yet</h2>
            <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">Create your first AI companion to get started.</p>
            <Button data-testid="empty-create-btn" onClick={() => navigate("/onboarding")} className="bg-gradient-to-r from-cyan-400 to-violet-500 text-white rounded-lg px-6 h-10 text-sm shadow-glow-sm hover:-translate-y-0.5 transition-all press-down">
              Create Your First Companion
            </Button>
          </div>
        ) : viewMode === "grid" ? (
          /* ─── Grid View ─── */
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="assistants-grid">
            {filtered.map((a, i) => (
              <div key={a.id} className={`rounded-xl overflow-hidden card-hover group animate-fade-in-up stagger-${Math.min(i + 1, 12)}`} data-testid={`assistant-card-${a.slug}`}>
                {/* Gradient Header */}
                <div className={`bg-gradient-to-r ${PURPOSE_GRADIENTS[a.purpose] || PURPOSE_GRADIENTS.custom} px-5 pt-4 pb-3`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 min-w-0">
                      <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center text-white text-lg font-medium group-hover:scale-105 transition-transform">
                        {a.name?.charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        {renamingId === a.id ? (
                          <div className="flex items-center gap-1">
                            <input ref={renameRef} value={renameValue} onChange={(e) => setRenameValue(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") saveRename(a); if (e.key === "Escape") setRenamingId(null); }} onBlur={() => saveRename(a)} className="font-outfit text-base font-medium text-white bg-white/20 border border-white/30 rounded px-1.5 py-0.5 w-36 focus:outline-none placeholder:text-white/50" data-testid={`rename-input-${a.slug}`} />
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5">
                            <h3 className="font-outfit text-base font-medium text-white truncate" data-testid={`card-name-${a.slug}`}>{a.name}</h3>
                            <button onClick={() => startRename(a)} className="opacity-0 group-hover:opacity-100 text-white/60 hover:text-white bg-transparent border-0 cursor-pointer transition-all" data-testid={`rename-btn-${a.slug}`} title="Rename">
                              <Pencil className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                        <p className="text-xs text-white/70 mt-0.5">{PURPOSE_LABELS[a.purpose] || a.purpose?.replace(/_/g, " ")}</p>
                      </div>
                    </div>
                    <div className={`flex items-center text-[10px] px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${a.is_active ? "bg-white/20 text-white" : "bg-black/10 text-white/60"}`}>
                      <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${a.is_active ? "bg-emerald-300 animate-pulse-dot" : "bg-white/40"}`} />
                      {a.is_active ? "Active" : "Inactive"}
                    </div>
                  </div>
                </div>
                {/* Card Body */}
                <div className="bg-card border border-border/60 border-t-0 rounded-b-xl px-5 py-4">

                {/* Badges */}
                <div className="flex gap-1.5 mb-3 flex-wrap">
                  <span className={`flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium border ${MODEL_BADGE_COLORS[normalizeModelId(a.ai_model)] || "bg-muted/70 text-muted-foreground border-border"}`}>
                    <Cpu className="w-2.5 h-2.5" /> {getModelName(a.ai_model)}
                  </span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium border ${TIER_COLORS[a.subscription_tier] || "bg-muted/70 text-muted-foreground border-border"}`}>
                    {a.subscription_tier}
                  </span>
                </div>

                {/* Last Message Preview */}
                {a.last_message && (
                  <div className="mb-3 bg-muted/40 rounded-lg px-3 py-2" data-testid={`last-msg-${a.slug}`}>
                    <p className="text-[11px] text-muted-foreground truncate">{a.last_message}</p>
                  </div>
                )}

                {/* Stats Row */}
                <div className="flex items-center gap-4 text-xs text-muted-foreground mb-3">
                  <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" /> {a.message_count || 0}</span>
                  <span className="flex items-center gap-1"><BarChart3 className="w-3 h-3" /> {a.conversation_count || 0}</span>
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {formatRelativeTime(a.last_active)}</span>
                </div>

                {/* URL */}
                <div className="flex items-center gap-1 mb-4">
                  <span className="text-[11px] text-muted-foreground/70 truncate">app-mi.ai/{a.slug}</span>
                  <button onClick={() => copyUrl(a.slug)} className="text-muted-foreground/70 hover:text-cyan-400 bg-transparent border-0 cursor-pointer transition-colors" data-testid={`copy-url-${a.slug}`} title="Copy URL">
                    {copiedSlug === a.slug ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
                  </button>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <Link to={`/chat/${a.slug}`} className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg bg-gradient-to-r from-cyan-400 to-violet-500 text-white text-xs font-medium no-underline hover:-translate-y-0.5 transition-all shadow-sm press-down" data-testid={`chat-btn-${a.slug}`}>
                    <Zap className="w-3.5 h-3.5" /> Chat
                  </Link>
                  <button onClick={() => handleGenerateAIPost(a)} className="flex items-center justify-center gap-1 px-3 py-2.5 rounded-lg border border-violet-500/30 text-violet-400 text-xs font-medium bg-violet-500/10 hover:bg-violet-500/15 cursor-pointer transition-all hover:-translate-y-0.5 border-0" data-testid={`gen-post-btn-${a.slug}`}>
                    <Sparkles className="w-3.5 h-3.5" /> Post
                  </button>
                  <Link to={`/publish?ai=${a.id}`} className="flex items-center justify-center gap-1 px-3 py-2.5 rounded-lg bg-gradient-to-r from-pink-500 to-rose-500 text-white text-xs font-medium no-underline transition-all hover:-translate-y-0.5 shadow-sm press-down" data-testid={`social-btn-${a.slug}`}>
                    <Send className="w-3.5 h-3.5" /> Publish
                  </Link>
                  <Link to={`/assistant/${a.id}/edit`} className="flex items-center justify-center gap-1 px-3 py-2.5 rounded-lg border border-border text-muted-foreground text-xs no-underline hover:bg-white/5 hover:-translate-y-0.5 transition-all" data-testid={`edit-btn-${a.slug}`}>
                    <Edit className="w-3.5 h-3.5" />
                  </Link>
                  <button onClick={() => copyUrl(a.slug)} className="flex items-center justify-center px-3 py-2.5 rounded-lg border border-border text-muted-foreground/70 hover:text-cyan-400 hover:border-cyan-500/30 hover:bg-cyan-500/10 bg-transparent cursor-pointer transition-all" data-testid={`share-btn-${a.slug}`} title="Share">
                    <Share2 className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => setDeleteTarget(a)} className="flex items-center justify-center px-3 py-2.5 rounded-lg border border-border text-muted-foreground/70 hover:text-red-500 hover:border-red-500/30 hover:bg-red-500/10 bg-transparent cursor-pointer transition-all" data-testid={`delete-btn-${a.slug}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                {/* end card body */}
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* ─── List View ─── */
          <div className="bg-card border border-border/60 rounded-xl overflow-hidden" data-testid="assistants-list">
            {filtered.map((a, i) => (
              <div key={a.id} className={`flex items-center gap-4 px-5 py-4 hover:bg-white/5 transition-colors ${i > 0 ? "border-t border-border/40" : ""}`} data-testid={`list-item-${a.slug}`}>
                <div className={`flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-400 to-violet-500 flex items-center justify-center text-white text-sm font-medium border-l-[3px] ${MODEL_STRIPE[normalizeModelId(a.ai_model)] || ""}`}>
                  {a.name?.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-outfit text-sm font-medium text-foreground truncate">{a.name}</h3>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium border ${MODEL_BADGE_COLORS[normalizeModelId(a.ai_model)] || "bg-muted/70 text-muted-foreground border-border"}`}>{getModelName(a.ai_model)}</span>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium border ${TIER_COLORS[a.subscription_tier] || ""}`}>{a.subscription_tier}</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground truncate mt-0.5">{a.last_message || PURPOSE_LABELS[a.purpose] || a.purpose?.replace(/_/g, " ")}</p>
                </div>
                <div className="flex items-center gap-3 text-[11px] text-muted-foreground/70 flex-shrink-0">
                  <span>{a.message_count || 0} msgs</span>
                  <span>{formatRelativeTime(a.last_active)}</span>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <Link to={`/chat/${a.slug}`} className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-cyan-400 to-violet-500 text-white text-[11px] font-medium no-underline hover:-translate-y-0.5 transition-all" data-testid={`list-chat-${a.slug}`}>Chat</Link>
                  <Link to={`/publish?ai=${a.id}`} className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-pink-500 to-rose-500 text-white text-[11px] font-medium no-underline hover:-translate-y-0.5 transition-all flex items-center gap-1" data-testid={`list-social-${a.slug}`}><Send className="w-3 h-3" /></Link>
                  <Link to={`/assistant/${a.id}/edit`} className="p-1.5 rounded-lg border border-border text-muted-foreground/70 hover:text-muted-foreground no-underline hover:bg-white/5 transition-all" data-testid={`list-edit-${a.slug}`}><Edit className="w-3.5 h-3.5" /></Link>
                  <button onClick={() => setDeleteTarget(a)} className="p-1.5 rounded-lg border border-border text-muted-foreground/70 hover:text-red-500 hover:border-red-500/30 bg-transparent cursor-pointer transition-all" data-testid={`list-delete-${a.slug}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent className="bg-card border border-border/60 rounded-2xl text-foreground shadow-xl" data-testid="delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit text-foreground">Delete Assistant</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Are you sure you want to delete <strong className="text-foreground">{deleteTarget?.name}</strong>? This will also delete all conversations and messages. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} className="border-border text-foreground/80 bg-card hover:bg-white/5 press-down" data-testid="delete-cancel-btn">Cancel</Button>
            <Button onClick={handleDelete} disabled={deleting} className="bg-red-500 hover:bg-red-600 text-white press-down" data-testid="delete-confirm-btn">
              {deleting ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Auto-Post Modal */}
      {aiPostModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => !aiPostModal.loading && setAiPostModal(null)} data-testid="ai-post-modal-dashboard">
          <div className="bg-card rounded-2xl shadow-xl max-w-lg w-full p-6 animate-fade-in-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-400 to-violet-500 flex items-center justify-center text-white font-medium">{aiPostModal.assistant.name?.charAt(0)}</div>
              <div>
                <p className="text-sm font-semibold text-foreground">{aiPostModal.assistant.name}</p>
                <p className="text-[11px] text-muted-foreground">AI-generated post preview</p>
              </div>
            </div>
            {aiPostModal.loading ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 text-cyan-400 animate-spin" /><span className="text-sm text-muted-foreground ml-2">Generating...</span></div>
            ) : (
              <>
                <textarea data-testid="dashboard-ai-post-textarea" value={aiPostText} onChange={(e) => setAiPostText(e.target.value)} rows={4} className="w-full bg-muted/40 border border-border rounded-lg px-4 py-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-cyan-400/25 resize-none mb-4" />
                <div className="flex items-center justify-between">
                  <button onClick={() => handleGenerateAIPost(aiPostModal.assistant)} className="text-xs text-cyan-400 font-medium bg-transparent border-0 cursor-pointer" data-testid="dashboard-ai-regenerate">Regenerate</button>
                  <div className="flex gap-2">
                    <button onClick={() => setAiPostModal(null)} className="px-4 py-2 text-xs text-muted-foreground bg-transparent border-0 cursor-pointer">Cancel</button>
                    <Button onClick={handlePublishAIPost} disabled={!aiPostText.trim()} className="bg-gradient-to-r from-cyan-400 to-violet-500 text-white text-xs px-4 h-8 rounded-lg disabled:opacity-40 press-down" data-testid="dashboard-ai-publish">Publish to Feed</Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
